import { setLoading } from '../index';
import uniq from 'lodash/uniq';
import { showExpressError, showErrorMessage } from '../../util/errorPopupUtil';
import { extractSelectedTicketFromState } from '../../extractFromState';
import * as types from '../types';
import { ZENDESK } from '../../constants/conversationTypes';
import { setOrders } from './OrdersSearchActions';
import { ErrorStrings } from '../../constants/fixedMessages';
import { setDummyButtonZendeskTags } from 'api/tickets/zendesk';
import { zendeskGetQuery } from 'api';
import { handleError } from 'services/ErrorService/ErrorService';

export const setCurrentPage = (currentPage, selectedArticle) => {
  return function (dispatch) {
    dispatch({
      type: 'ZENDESK_CURRENT_PAGE',
      currentPage: currentPage,
      selectedArticle: selectedArticle,
    });
  };
};

export const getHelpCenterCategories = (shopName, simplrAccountId) => {
  return function (dispatch) {
    const query = {
      shop: shopName,
      simplrAccountId: simplrAccountId,
    };

    return zendeskGetQuery('/help_center/categories', query, 'categories')
      .then((responseBody) => {
        dispatch({
          type: 'ZENDESK_CATEGORIES',
          categories: responseBody,
        });
      })
      .catch((err) => {
        handleError({ error: err, customMessage: 'Error getting help center categories' });
        dispatch({
          type: 'ZENDESK_CATEGORIES',
          categories: [],
        });
      });
  };
};

export const getHelpCenterSections = (shopName, simplrAccountId) => {
  return function (dispatch) {
    const query = {
      shop: shopName,
      simplrAccountId: simplrAccountId,
    };

    return zendeskGetQuery('/help_center/sections', query, 'sections')
      .then((responseBody) => {
        dispatch({
          type: 'ZENDESK_SECTIONS',
          sections: responseBody,
        });
      })
      .catch((err) => {
        handleError({ error: err, customMessage: 'Error getting help center sections' });
        dispatch({
          type: 'ZENDESK_SECTIONS',
          sections: [],
        });
      });
  };
};

export const getHelpCenterArticlesBySection = (shopName, sectionId, simplrAccountId) => {
  return function (dispatch, getState) {
    const query = {
      shop: shopName,
      simplrAccountId: simplrAccountId,
    };

    return zendeskGetQuery(`/help_center/sections/${sectionId}/articles`, query, 'articles')
      .then((responseBody) => {
        dispatch({
          type: 'ZENDESK_ARTICLES',
          articles: responseBody,
        });
        const authorIds = uniq(responseBody.map((article) => article.author_id));

        return authorIds.map((authorId) => {
          const zendeskUsers = getState().tools.zendesk.users;
          if (!zendeskUsers[authorId]) {
            return zendeskGetQuery(`/users/${authorId}`, query, 'user').then((user) => {
              dispatch({
                type: 'ZENDESK_USER',
                user: user,
              });
            });
          }

          return undefined;
        });
      })
      .catch((err) => {
        handleError({ error: err, customMessage: 'Error getting help center articles by section', showPopup: true });
      });
  };
};

export const getCustomerOrdersFromZendesk = (shopName, query) => {
  return function (dispatch, getState) {
    let combinedQuery;
    let ticket = extractSelectedTicketFromState(getState());
    if (!ticket) {
      dispatch(setLoading(false, 'searchPage'));

      return showErrorMessage(ErrorStrings.SELECTED_TICKET_NOT_FOUND);
    }
    combinedQuery = Object.assign({}, query, {
      shop: shopName,
      simplrAccountId: ticket.simplrAccountPtrId,
    });

    return zendeskGetQuery('/getCustomerOrders', combinedQuery)
      .then((orders) => {
        dispatch(setLoading(false, 'searchPage'));
        orders.forEach((order, index) => {
          if (index !== 0) {
            order['zipCode'] = 'Not Available (Try with the Zip Code used in the latest order or ask customer)';
            order['productTitles'] = 'Not Available (optional)';
          }
        });
        dispatch(setOrders(orders));
      })
      .catch((err) => {
        handleError({ error: err, customMessage: 'Error getting help center categories' });
        dispatch(setLoading(false, 'searchPage'));
        if (err.status === 404) {
          let orders = [];
          orders.push({
            orderId: 'Not Available (Ask customer - Not applicable to eBay or Amazon orders)',
            productTitles: 'Not Available (optional)',
            zipCode: 'Not Available (Ask customer - Not applicable to eBay or Amazon orders)',
            errorMessage: 'No matching order found for this email id.',
          });

          return dispatch(setOrders(orders));
        }
        showExpressError(err);
      });
  };
};

export const hydrateZendeskDropdowns = (
  shopName,
  simplrAccountId,
  menuId = null,
  useSmbFilter = true,
  dropdownType = null
) => {
  return async (dispatch) => {
    const query = {
      shop: shopName,
      simplrAccountId,
      useSmbFilter,
      menuId,
    };
    try {
      const results = await zendeskGetQuery('/getTicketFieldById', query);

      if (dropdownType === 'REFUND') {
        dispatch(hydrateZendeskRefundDropdown(results.ticket_field.custom_field_options));
      } else if (dropdownType === 'CANCEL') {
        dispatch(hydrateZendeskCancelDropdown(results.ticket_field.custom_field_options));
      }

      return results.ticket_field.custom_field_options;
    } catch (error) {
      handleError({ error, showPopup: true });

      return null;
    }
  };
};

const hydrateZendeskRefundDropdown = (data) => ({
  type: 'HYDRATE_ZENDESK_REFUND_DROPDOWN',
  data,
});

const hydrateZendeskCancelDropdown = (data) => ({
  type: 'HYPDRATE_ZENDESK_CANCEL_DROPDOWN',
  data,
});

export const clearPartnerTags = () => {
  return {
    type: types.CLEAR_PARTNER_TAGS,
  };
};
// // TODO: where is dispatch? Why this is not action creator?
export const updateDummyButtonZendeskTags = async (processedTicket, tags) => {
  // TODO: Can we read ticket from state?
  if (!processedTicket || processedTicket.ticketType !== ZENDESK) {
    throw new Error('Ticket not found, or it is a non-ZENDESK ticket');
  }

  const payload = {
    tags,
    conversationId: processedTicket.ticketId,
  };

  await setDummyButtonZendeskTags(payload);
};

export const clearZendesk = () => {
  return {
    type: 'ZENDESK_CLEAR',
  };
};
